/**
 * Iter 55 — Tenant Owner / Admin user-management page.
 *
 * Capabilities:
 *   - List users (status, role, last_login_at)
 *   - Invite user (email + name + role) → console-mode invite URL shown inline
 *   - Disable user / reactivate
 *   - Change role (owner-only for granting 'owner')
 *   - List + revoke pending invitations
 *   - Toggle modules (owner only)
 */
import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Plus, Copy, ShieldOff, ShieldCheck, X, LayoutGrid } from "lucide-react";
import { toast } from "sonner";
import SheetAssignmentModal from "@/components/admin/SheetAssignmentModal";

const RESTRICTED_ROLES = ["subcontractor", "foreman"];

const ALL_ROLES = [
  "owner", "executive", "admin", "project_manager", "superintendent",
  "field_engineer", "safety_manager", "inspector", "foreman",
  "subcontractor", "viewer",
];
const ROLE_LABEL = {
  owner: "Owner", executive: "Executive", admin: "Admin",
  project_manager: "Project Manager", superintendent: "Superintendent",
  field_engineer: "Field Engineer", safety_manager: "Safety Manager",
  inspector: "Inspector", foreman: "Foreman",
  subcontractor: "Subcontractor", viewer: "Viewer",
};
const ALL_MODULES = [
  "projects", "plans", "pins", "workflows", "rfi", "photos",
  "objects", "utility_intelligence", "measurements", "documents", "reports",
];

export default function TenantAdmin() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [invs, setInvs] = useState([]);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);
  const [lastInvite, setLastInvite] = useState(null);

  const canManage = ["owner", "admin"].includes(user?.role);
  const isOwner = user?.role === "owner";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, i, t] = await Promise.all([
        api.get("/tenant/users"),
        api.get("/tenant/users/invitations"),
        api.get("/tenant"),
      ]);
      setUsers(u.data);
      setInvs(i.data);
      setTenant(t.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!canManage) {
    return (
      <div className="p-8 text-[var(--fop-text-2)]" data-testid="tenant-admin-locked">
        Tenant Admin access required.
      </div>
    );
  }

  return (
    <div className="p-6 space-y-8 max-w-5xl mx-auto" data-testid="tenant-admin">
      <header>
        <div className="text-sm uppercase tracking-[0.18em] text-[var(--fop-text-2)] font-semibold">Tenant Administration</div>
        <h1 className="text-3xl font-semibold text-[var(--fop-text)] mt-1">{tenant?.display_name}</h1>
        <div className="text-base text-[var(--fop-text-2)] mt-1">{users.length} users · {invs.filter(i => i.status === "pending").length} pending invitations</div>
      </header>

      <InvitePanel onCreated={(inv) => { setLastInvite(inv); load(); }}
                    isOwner={isOwner} />

      {lastInvite && <LastInviteBanner inv={lastInvite} onClose={() => setLastInvite(null)} />}

      <UsersTable users={users} loading={loading} actor={user}
                    onChanged={load} />

      <InvitationsTable invs={invs} onChanged={load} />

      {isOwner && tenant && (
        <ModulesPanel tenant={tenant} onChanged={load} />
      )}
    </div>
  );
}

function InvitePanel({ onCreated, isOwner }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("viewer");
  const [submitting, setSubmitting] = useState(false);

  // Tenant admin can't grant owner. Filter the role options accordingly.
  const grantableRoles = isOwner
    ? ALL_ROLES
    : ALL_ROLES.filter((r) => r !== "owner");

  async function submit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/tenant/users/invite", {
        email: email.trim(), name: name.trim(), role,
      });
      onCreated(data);
      setEmail(""); setName(""); setRole("viewer");
      toast.success("Invitation created");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invite failed");
    } finally { setSubmitting(false); }
  }

  return (
    <section data-testid="invite-panel" className="rounded-xl border border-[var(--fop-border)] bg-[var(--fop-surface)] p-5">
      <h2 className="text-sm font-semibold mb-3 text-[var(--fop-text)]">Invite user</h2>
      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
        <div className="md:col-span-4">
          <Label className="text-[10px] uppercase tracking-[0.18em] text-[var(--fop-text-3)]">Email</Label>
          <Input type="email" required value={email}
                  data-testid="invite-email"
                  onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="md:col-span-4">
          <Label className="text-[10px] uppercase tracking-[0.18em] text-[var(--fop-text-3)]">Name</Label>
          <Input required value={name}
                  data-testid="invite-name"
                  onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="md:col-span-3">
          <Label className="text-[10px] uppercase tracking-[0.18em] text-[var(--fop-text-3)]">Role</Label>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger data-testid="invite-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              {grantableRoles.map((r) => (
                <SelectItem key={r} value={r}>{ROLE_LABEL[r] || r}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-1">
          <Button type="submit" disabled={submitting}
                   data-testid="invite-submit"
                   className="w-full bg-[var(--fop-primary)]">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          </Button>
        </div>
      </form>
    </section>
  );
}

function LastInviteBanner({ inv, onClose }) {
  const fullUrl = `${window.location.origin}${inv.url}`;
  return (
    <section data-testid="last-invite-banner"
              className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-5 relative">
      <button onClick={onClose} className="absolute top-3 right-3 text-[var(--fop-text-3)]"
              data-testid="last-invite-close">
        <X className="w-4 h-4" />
      </button>
      <div className="text-xs uppercase tracking-[0.18em] text-emerald-400 mb-1">Invitation ready</div>
      <div className="text-sm text-[var(--fop-text)] mb-3">
        Send this link to <span className="font-mono">{inv.email}</span>. Expires {new Date(inv.expires_at).toLocaleString()}.
      </div>
      <div className="flex gap-2 items-center">
        <code className="flex-1 truncate rounded bg-[var(--fop-surface-2)] px-3 py-2 text-xs"
              data-testid="invite-link">{fullUrl}</code>
        <Button size="sm" variant="outline"
                 data-testid="invite-copy"
                 onClick={() => { navigator.clipboard.writeText(fullUrl); toast.success("Copied"); }}>
          <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy
        </Button>
      </div>
    </section>
  );
}

function UsersTable({ users, loading, actor, onChanged }) {
  return (
    <section data-testid="users-table"
              className="rounded-xl border border-[var(--fop-border)] bg-[var(--fop-surface)] p-5">
      <h2 className="text-sm font-semibold mb-3 text-[var(--fop-text)]">Users</h2>
      {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
        <div className="divide-y divide-[var(--fop-border)]">
          {users.map((u) => (
            <UserRow key={u.id} u={u} actor={actor} onChanged={onChanged} />
          ))}
        </div>
      )}
    </section>
  );
}

function UserRow({ u, actor, onChanged }) {
  const [pendingRole, setPendingRole] = useState(null);
  const [sheetAccessOpen, setSheetAccessOpen] = useState(false);
  const isSelf = u.id === actor.id;
  const actorIsOwner = actor.role === "owner";
  const targetIsOwner = u.role === "owner";
  // Admin can't touch owners; admin can't grant owner.
  const canEdit = actorIsOwner || (!targetIsOwner && !isSelf);
  const grantable = actorIsOwner
    ? ALL_ROLES
    : ALL_ROLES.filter((r) => r !== "owner");

  async function saveRole(newRole) {
    try {
      await api.patch(`/users/${u.id}`, { role: newRole });
      toast.success(`Role set to ${ROLE_LABEL[newRole]}`);
      setPendingRole(null);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  }
  async function toggleStatus() {
    const next = u.status === "disabled" ? "active" : "disabled";
    try {
      await api.patch(`/users/${u.id}`, { status: next });
      toast.success(next === "active" ? "User reactivated" : "User disabled");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  }
  return (
    <div className="flex items-center gap-4 py-4" data-testid={`user-row-${u.id}`}>
      <div className="flex-1 min-w-0">
        <div className="text-base text-[var(--fop-text)] font-semibold truncate">{u.name}</div>
        <div className="text-sm text-[var(--fop-text-2)] font-mono truncate">{u.email}</div>
      </div>
      <div className="text-sm text-[var(--fop-text-2)] hidden sm:block w-32 font-medium">
        {u.last_login_at ? new Date(u.last_login_at).toLocaleDateString() : "never"}
      </div>
      <Select value={pendingRole ?? u.role}
               disabled={!canEdit}
               onValueChange={(v) => { setPendingRole(v); saveRole(v); }}>
        <SelectTrigger className="w-40" data-testid={`role-select-${u.id}`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {grantable.map((r) => (
            <SelectItem key={r} value={r}>{ROLE_LABEL[r] || r}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <span className={`text-xs uppercase tracking-[0.14em] w-20 text-center font-bold ${
        u.status === "disabled" ? "text-[var(--fop-warning)]" :
        u.status === "pending" ? "text-[var(--fop-info)]" : "text-[var(--fop-success)]"
      }`} data-testid={`user-status-${u.id}`}>
        {u.status || "active"}
      </span>
      <Button size="sm" variant="outline" disabled={!canEdit}
               onClick={toggleStatus}
               data-testid={`toggle-status-${u.id}`}
               className="border-[var(--fop-border)]">
        {u.status === "disabled" ? <ShieldCheck className="w-4 h-4" /> : <ShieldOff className="w-4 h-4" />}
      </Button>
      {RESTRICTED_ROLES.includes(pendingRole ?? u.role) && (
        <Button size="sm" variant="outline"
                 onClick={() => setSheetAccessOpen(true)}
                 data-testid={`sheet-access-btn-${u.id}`}
                 title="Manage which sheets this user can see and work with"
                 className="border-[var(--fop-border)]">
          <LayoutGrid className="w-4 h-4" />
        </Button>
      )}
      <SheetAssignmentModal open={sheetAccessOpen} onClose={() => setSheetAccessOpen(false)} targetUser={u} />
    </div>
  );
}

function InvitationsTable({ invs, onChanged }) {
  const pending = invs.filter((i) => i.status === "pending");
  if (!pending.length) return null;
  async function revoke(id) {
    try {
      await api.post(`/tenant/users/invitations/${id}/revoke`);
      toast.success("Invitation revoked");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  }
  async function resend(id) {
    try {
      const { data } = await api.post(`/tenant/users/invitations/${id}/resend`);
      const fullUrl = `${window.location.origin}${data.url}`;
      navigator.clipboard.writeText(fullUrl);
      toast.success("New link copied to clipboard");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  }
  return (
    <section data-testid="invitations-table"
              className="rounded-xl border border-[var(--fop-border)] bg-[var(--fop-surface)] p-5">
      <h2 className="text-sm font-semibold mb-3 text-[var(--fop-text)]">Pending invitations</h2>
      <div className="divide-y divide-[var(--fop-border)]">
        {pending.map((i) => (
          <div key={i.id} className="flex items-center gap-3 py-3" data-testid={`inv-row-${i.id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{i.name}</div>
              <div className="text-xs text-[var(--fop-text-2)] font-mono truncate">{i.email}</div>
            </div>
            <div className="text-xs text-[var(--fop-text-3)] hidden sm:block w-32">{ROLE_LABEL[i.role] || i.role}</div>
            <Button size="sm" variant="outline" onClick={() => resend(i.id)}
                     data-testid={`inv-resend-${i.id}`}>Resend</Button>
            <Button size="sm" variant="outline" onClick={() => revoke(i.id)}
                     data-testid={`inv-revoke-${i.id}`}>Revoke</Button>
          </div>
        ))}
      </div>
    </section>
  );
}

function ModulesPanel({ tenant, onChanged }) {
  const [modules, setModules] = useState(tenant.modules_enabled || []);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setModules(tenant.modules_enabled || []); }, [tenant]);
  function toggle(m) {
    setModules((prev) => prev.includes(m)
      ? prev.filter((x) => x !== m)
      : [...prev, m]);
  }
  async function save() {
    setSaving(true);
    try {
      await api.patch("/tenant", { modules_enabled: modules });
      toast.success("Modules updated");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  }
  return (
    <section data-testid="modules-panel"
              className="rounded-xl border border-[var(--fop-border)] bg-[var(--fop-surface)] p-5">
      <h2 className="text-sm font-semibold mb-3 text-[var(--fop-text)]">Modules</h2>
      <div className="text-xs text-[var(--fop-text-3)] mb-3">
        Disabled modules hide their UI and reject their API routes (404). Save to apply.
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {ALL_MODULES.map((m) => (
          <label key={m} className="flex items-center gap-2 text-sm cursor-pointer"
                  data-testid={`module-toggle-${m}`}>
            <input type="checkbox" checked={modules.includes(m)}
                    onChange={() => toggle(m)} />
            <span>{m.replace(/_/g, " ")}</span>
          </label>
        ))}
      </div>
      <Button onClick={save} disabled={saving} className="mt-4 bg-[var(--fop-primary)]"
               data-testid="modules-save">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save modules"}
      </Button>
    </section>
  );
}
